/**
 * Shared Condition Card Components
 *
 * Extracted from ConditionsSection for reuse across audience-specific
 * section components (Internal, Title/Escrow, Borrower).
 */

import { CheckCircle2, Circle, Download, Upload, FileText, AlertCircle, FileSignature, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import type { EncompassCondition, BorrowerPairInfo } from '@/types/approval-letter';
import type { ConditionTemplateInfo, ConditionsPortalUpload } from '@/types/conditions-portal';

export interface ConditionsSectionProps {
  title: string;
  conditions: EncompassCondition[];
  templates: Record<string, ConditionTemplateInfo>;
  uploads: ConditionsPortalUpload[];
  /** Notes added by lender when routing to title/escrow (keyed by externalId) */
  notes?: Record<string, string>;
  onDownloadTemplate: (externalId: string, templateName: string, borrowerPairId?: string) => void;
  onUploadClick: (condition: EncompassCondition) => void;
  /** Handler for interactive warranty forms (only provided for admin/underwriter roles) */
  onFillAndSign?: (condition: EncompassCondition) => void;
  /** Borrower pairs for multi-pair template downloads */
  borrowerPairs?: BorrowerPairInfo[];
}

export interface ConditionCardProps {
  condition: EncompassCondition;
  template?: ConditionTemplateInfo;
  uploads: ConditionsPortalUpload[];
  /** Note added by lender when routing to title/escrow */
  note?: string;
  onDownloadTemplate: (externalId: string, templateName: string, borrowerPairId?: string) => void;
  onUploadClick: (condition: EncompassCondition) => void;
  onFillAndSign?: (condition: EncompassCondition) => void;
  /** Borrower pairs for multi-pair template downloads */
  borrowerPairs?: BorrowerPairInfo[];
}

export const ConditionCard = ({
  condition,
  template,
  uploads,
  note,
  onDownloadTemplate,
  onUploadClick,
  onFillAndSign,
  borrowerPairs
}: ConditionCardProps) => {
  const isOpen = condition.statusOpen;
  const hasTemplate = template?.hasTemplate;
  const hasUploads = uploads.length > 0;

  // Get display description (prefer external, fallback to internal)
  const description = condition.externalDescription || condition.internalDescription || condition.title;

  return (
    <div
      className={`
        p-3 rounded-lg border transition-all
        ${isOpen
          ? 'bg-white border-gray-200 hover:border-cyan-300 hover:shadow-sm'
          : 'bg-gray-50 border-gray-100'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        <div className="mt-0.5">
          {isOpen ? (
            <Circle className="h-4 w-4 text-amber-500" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div>
            <p className={`text-sm font-medium ${isOpen ? 'text-gray-900' : 'text-gray-500'}`}>
              {condition.title}
            </p>
            {description !== condition.title && (
              <p className="text-sm text-gray-500 mt-1">
                {description}
              </p>
            )}
            {/* Category & Source Metadata */}
            {(condition.category || condition.sourceOfCondition) && (
              <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-gray-400">
                {condition.category && (
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">
                    {condition.category}
                  </span>
                )}
                {condition.sourceOfCondition && (
                  <span>
                    Source: {condition.sourceOfCondition === 'AutomatedByRule' ? 'Automated' : condition.sourceOfCondition}
                  </span>
                )}
              </div>
            )}

            {/* Note from Lender (for title/escrow portal) */}
            {note && (
              <div className="mt-2 p-2 rounded border border-blue-200 bg-blue-50/50">
                <p className="text-xs font-medium text-blue-700 mb-0.5">Note from Lender</p>
                <p className="text-sm text-blue-900">{note}</p>
              </div>
            )}
          </div>

          {/* Actions - only for open conditions */}
          {isOpen && (
            <div className="flex items-center gap-2 mt-3">
              {/* Fill & Sign Button (for interactive forms like 0413 Condo/PUD Warranty) */}
              {condition.externalId === '0413' && onFillAndSign && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  onClick={() => onFillAndSign(condition)}
                >
                  <FileSignature className="h-3 w-3 mr-1" />
                  Fill & Sign
                </Button>
              )}

              {/* Download Template Button — per-pair dropdown when multi-pair */}
              {hasTemplate && borrowerPairs && borrowerPairs.length > 1 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs">
                      <Download className="h-3 w-3 mr-1" />
                      Choose borrower pair
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {borrowerPairs.map((pair) => (
                      <DropdownMenuItem
                        key={pair.applicationId || pair.index}
                        onClick={() => onDownloadTemplate(condition.externalId, template?.templateName || 'Template', pair.applicationId)}
                      >
                        {pair.displayLabel}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : hasTemplate ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onDownloadTemplate(condition.externalId, template?.templateName || 'Template')}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Template
                </Button>
              ) : null}

              {/* Upload Button */}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                onClick={() => onUploadClick(condition)}
              >
                <Upload className="h-3 w-3 mr-1" />
                Upload Document
              </Button>
            </div>
          )}

          {/* Show uploads */}
          {hasUploads && (
            <div className="mt-2 space-y-1">
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded px-2 py-1"
                >
                  <FileText className="h-3 w-3" />
                  <span className="truncate flex-1">{upload.originalFilename}</span>
                  <SyncStatusBadge status={upload.encompassSyncStatus} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SyncStatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case 'synced':
      return (
        <span className="inline-flex items-center gap-1 text-green-600">
          <CheckCircle2 className="h-3 w-3" />
          Synced
        </span>
      );
    case 'syncing':
      return (
        <span className="inline-flex items-center gap-1 text-blue-600">
          <Circle className="h-3 w-3 animate-pulse" />
          Syncing
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 text-red-600">
          <AlertCircle className="h-3 w-3" />
          Failed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 text-gray-500">
          <Circle className="h-3 w-3" />
          Pending
        </span>
      );
  }
};
